<template>
  <div class="add-new-shop-item flex flex-col w-full h-full gap-2 overflow-y-auto">
    <div class="flex flex-col">
      <div class="service-info gap-2">
        <div class="flex flex-col">
          <span class="font-medium mb-1">שם המוצר</span>
          <input-text
            v-model="(newShopItem as IShopItem).name"
            class="p-inputtext-sm"
            placeholder="שם המוצר"
            type="text"
          />
          <small v-show="nameError" class="error">{{ nameError }}</small>
        </div>

        <div class="flex flex-col">
          <span class="mt-3 mb-1 font-medium">תיאור המוצר</span>
          <Textarea
            v-model="(newShopItem as IShopItem).description"
            :autoResize="true"
            rows="2"
            placeholder="תיאור המוצר"
          />
          <small v-show="descriptionError" class="error">{{ descriptionError }}</small>
        </div>

        <div class="flex flex-col">
          <span class="mt-3 mb-1 font-medium">קטגוריות</span>
          <AutoComplete
            v-model="(newShopItem as IShopItem).categories"
            style="width: 100%"
            multiple
            class="flex-grow "
            :suggestions="items"
            :pt="{
              token: {
                class: 'flex flex-row-reverse gap-2 w-max',
                style: 'flex-grow:unset;'
              },
              tokenLabel: {
                class: 'text-sm'
              },
              removeTokenIcon: {
                style: 'width: 1rem; height: 1rem; margin:0;'
              },

          }"
            @complete="search"
          />
          <small v-show="categoriesError" class="error">{{ categoriesError }}</small>
        </div>

        <div v-if="isDiscount" class="flex gap-4">
          <div class="flex flex-col">
            <span class="font-medium ">מחיר</span>
            <div class="w-full relative">
              <input-number
                v-model="(newShopItem as IShopItem).price"
                :maxFractionDigits="2"
                :min="0"
                class="p-inputtext-sm"
                locale="he-IL"
                style="margin-bottom: 0; padding: 0"
              />
              <span class="bg-white absolute left-[0.75rem] top-[0.625rem]">ש"ח</span>
            </div>
          </div>
          <div class="flex flex-col">
            <span class="font-medium">מחיר מבצע</span>
            <div class="w-full relative">
              <input-number
                v-model="(newShopItem as IShopItem).discountedPrice"
                :maxFractionDigits="2"
                :min="0"
                class="p-inputtext-sm"
                locale="he-IL"
                style="margin-bottom: 0; padding: 0"
              />
              <span class="bg-white absolute left-[0.75rem] top-[0.625rem]">ש"ח</span>
            </div>
          </div>
        </div>
        <div v-else class="flex flex-col">
          <span class="font-medium">מחיר</span>
          <div class="w-full relative">
            <input-number
              v-model="(newShopItem as IShopItem).price"
              :maxFractionDigits="2"
              :min="0"
              class="p-inputtext-sm"
              locale="he-IL"
              style="margin-bottom: 0; padding: 0"
            />
            <span class="bg-white absolute left-[0.75rem] top-[0.625rem]">ש"ח</span>
          </div>
        </div>
        <small v-show="priceError" class="error">{{ priceError }}</small>

        <settings-toggle
          :status="isDiscount ?? false"
          title="מוצר זה במבצע"
          @value-changed="(val) => {isDiscount = val}"/>

        <div class="flex flex-col">
          <span class="mt-3 mb-1 font-medium">כמה יחידות במלאי</span>
          <div class="w-full relative">
            <input-number
              v-model="(newShopItem as IShopItem).availableStock"
              :min="0"
              :step="5"
              class="p-inputtext-sm"
              mode="decimal"
              style="margin-bottom: 0; padding: 0"
            />
            <span class="bg-white absolute left-[0.75rem] top-[0.625rem]">יחידות</span>
          </div>
          <small v-show="stockError" class="error">{{ stockError }}</small>
        </div>

        <div class="flex flex-col">
          <span class="mt-3 mb-1 font-medium">ברקוד (לא חובה)</span>
          <div class="flex gap-2 items-start">
            <input-text
              v-model="(newShopItem as IShopItem).barcode"
              class="p-inputtext-sm flex-grow"
              placeholder="הקלידו או סרקו את ברקוד המוצר"
              type="text"
              dir="ltr"
            />
            <Button
              v-if="canScanBarcode"
              icon="pi pi-barcode"
              size="small"
              outlined
              @click="scanBarcode" />
          </div>
          <small v-show="barcodeError" class="error">{{ barcodeError }}</small>
        </div>
      </div>

      <div class="flex flex-col">
        <span class="mt-3 mb-1 font-medium">העלאת תמונות</span>

        <div class="flex flex-wrap justify-between gap-3">
          <image-upload
            :uploadFunction="(imageUrl) => {uploadImage(1, imageUrl)}"
            :imageUrl="image1"
            origin="shop"
            class="max-w-[48%]"
            @remove="removeImage(1)"
          />
          <image-upload
            :uploadFunction="(imageUrl) => {uploadImage(2, imageUrl)}"
            :imageUrl="image2"
            origin="shop"
            class="max-w-[48%]"
            @remove="removeImage(2)"
          />
          <image-upload
            :uploadFunction="(imageUrl) => {uploadImage(3, imageUrl)}"
            :imageUrl="image3"
            origin="shop"
            class="max-w-[48%]"
            @remove="removeImage(3)"
          />
          <image-upload
            :uploadFunction="(imageUrl) => {uploadImage(4, imageUrl)}"
            :imageUrl="image4"
            origin="shop"
            class="max-w-[48%]"
            @remove="removeImage(4)"
          />
        </div>
      </div>
      <divider
        v-if="mode ==='edit'"
        :pt="{root:{style: 'margin: 1rem 0'}}">
        או
      </divider>
      <Button
        v-if="mode ==='edit'"
        label=" מחיקת מוצר "
        severity="danger"
        style="width: 100%; padding: 0;"
        text
        @click="removeShopItem"/>
    </div>

    <drawer-footer
      :approveLoading="ctaLoading"
      :approveLabel="mode === 'new' ? 'הוספת מוצר' : 'עריכת מוצר'"
      @approve-click="clickedSubmit"
      @cancel-click="$emit('close')"
    />
    <ConfirmDialog/>
  </div>
</template>

<script lang="ts" setup>
import type {IShopItem} from '@torim-app/types';

import {PropType, reactive, ref, watch} from 'vue';
import {useToast} from 'vue-toastification';
import {useAdminStore} from '@app/stores';
import {useConfirm} from "primevue/useconfirm";
import {ShopApi} from "@app/api/admin/shop-api";
import { trackEvent } from '@app/composables/posthog';
import ConfirmDialog from 'primevue/confirmdialog';
import { Capacitor } from '@capacitor/core';
import { PRODUCT_BARCODE_FORMATS, useQRScanner } from '@app/composables/qr-reader';

const toast = useToast();
const adminStore = useAdminStore();
const confirm = useConfirm();

const emit = defineEmits(['close'])
const props = defineProps({
  mode: {type: String as PropType<'edit' | 'new'>, required: false, default: 'new'},
  shopItem: {type: Object as PropType<IShopItem>, required: false},
})
const newShopItem = reactive<Partial<IShopItem>>(props.mode === 'edit' && props.shopItem ? {
  ...props.shopItem,
  images: [...props.shopItem.images]
} : <Partial<IShopItem>>{images: []});
const isDiscount = ref<boolean>(props.mode === 'edit' && props.shopItem?.discountedPrice !== undefined);

// Errors
const nameError = ref();
const priceError = ref();
const stockError = ref();
const descriptionError = ref();
const categoriesError = ref();
const barcodeError = ref();
const ctaLoading = ref<boolean>(false);

// Barcode scanning (native only — ML Kit)
const { scanSingleBarcode } = useQRScanner();
const canScanBarcode = Capacitor.isNativePlatform();

const scanBarcode = async () => {
  trackEvent('shop_product_barcode_scan_clicked');
  try {
    newShopItem.barcode = await scanSingleBarcode(PRODUCT_BARCODE_FORMATS);
  } catch (e) {
    console.error(e);
    toast.error('הסריקה נכשלה, אפשר להקליד את הברקוד ידנית');
  }
};

// Images
const image1 = ref<string>();
const image2 = ref<string>();
const image3 = ref<string>();
const image4 = ref<string>();

// Categories
const suggestions = ref<string[]>([]);
const items = ref<string[]>([]);

const search = (event: any) => {
  items.value = [...suggestions.value.filter(item => item.includes(event.query) && !newShopItem.categories?.includes(item)), event.query];
}

const resetFields = () => {
  descriptionError.value = null;
  stockError.value = null;
  priceError.value = null;
  nameError.value = null;
  categoriesError.value = null;
  barcodeError.value = null;
};

const removeShopItem = () => {
  confirm.require({
    message: 'האם למחוק את המוצר?',
    header: 'רק מוודאים',
    rejectLabel: 'לא',
    acceptLabel: 'כן',
    blockScroll: true,
    accept: async () => {
      try {
        if (!props.shopItem) return;

        await ShopApi.deactivateProduct(props.shopItem.id);
        await adminStore.fetchBusinessData();
        toast.success('המוצר נמחק בהצלחה');
        emit('close');
      } catch (e) {
        console.error(e)
        toast.error("לא הצלחנו למחוק את המוצר, אנא נסו שוב");
      }
    },
  });
}

const validateFields = () => {
  let isValid = true;

  if (!newShopItem.description || newShopItem.description === '') {
    descriptionError.value = 'יש להזין תיאור';
    isValid = false;
  }

  if (newShopItem.availableStock === null || newShopItem.availableStock === undefined || newShopItem.availableStock < 0) {
    stockError.value = 'יש להזין מלאי תקין';
    isValid = false;
  }

  if (!newShopItem.price || newShopItem.price < 0) {
    priceError.value = 'יש להזין מחיר תקין';
    isValid = false;
  }

  if (isDiscount.value && (!newShopItem.discountedPrice || newShopItem.discountedPrice < 0)) {
    priceError.value = 'יש להזין מחיר והנחה תקינים';
    isValid = false;
  }

  if (!newShopItem.name || newShopItem.name === '') {
    nameError.value = 'יש להזין שם עבור המוצר';
    isValid = false;
  }

  if (!newShopItem.categories || newShopItem.categories.length === 0) {
    categoriesError.value = 'יש לבחור לפחות קטגוריה אחת';
    isValid = false;
  }

  if (newShopItem.barcode?.trim()) {
    const duplicate = adminStore.businessSiteData?.shop?.items?.find(
      x => x.active && x.barcode === newShopItem.barcode!.trim() && x.id !== newShopItem.id
    );
    if (duplicate) {
      barcodeError.value = `הברקוד כבר משויך למוצר "${duplicate.name}"`;
      isValid = false;
    }
  }

  return isValid;
};

const clickedSubmit = async (): Promise<void> => {
  trackEvent('shop_product_saved');
  try {
    resetFields();
    if (!validateFields())
      return;

    ctaLoading.value = true;
    if (props.mode === 'new') {
      await ShopApi.addProduct(<IShopItem>newShopItem);
      toast.success('המוצר נוסף בהצלחה :)');
    } else {
      await ShopApi.editProduct(<IShopItem>newShopItem);
      toast.success('המוצר עודכן בהצלחה :)');
    }
    await adminStore.fetchBusinessData();
    ctaLoading.value = false;
    emit('close');
  } catch (e) {
    console.error(e);
    // Keep the drawer open so the owner doesn't lose everything they typed on a failure
    // they can simply retry.
    ctaLoading.value = false;
    toast.error(
      props.mode === 'new'
        ? 'לא הצלחנו להוסיף את המוצר, אנא נסו שוב'
        : 'לא הצלחנו לעדכן את המוצר, אנא נסו שוב'
    );
  }
};

const slotRefs = { 1: image1, 2: image2, 3: image3, 4: image4 } as const;

const syncImagesArray = () => {
  newShopItem.images = [image1.value, image2.value, image3.value, image4.value]
    .filter((url): url is string => !!url);
};

const uploadImage = (id: 1 | 2 | 3 | 4, imageUrl: string) => {
  slotRefs[id].value = imageUrl;
  syncImagesArray();
}

const removeImage = (id: 1 | 2 | 3 | 4) => {
  slotRefs[id].value = undefined;
  syncImagesArray();
}

suggestions.value.push(...new Set((adminStore.businessSiteData?.shop?.items?.map(x => x.categories) ?? []).flat()));
if (props.mode === 'edit' && props.shopItem) {
  for (let i = 1; i < props.shopItem.images.length + 1; i++) {
    if (i > 4) continue;
    console.log(i)
    if (i === 1) image1.value = props.shopItem.images[i - 1];
    if (i === 2) image2.value = props.shopItem.images[i - 1];
    if (i === 3) image3.value = props.shopItem.images[i - 1];
    if (i === 4) image4.value = props.shopItem.images[i - 1];
  }
}

watch(
  () => isDiscount.value,
  () => {

    if (!isDiscount.value)
      delete newShopItem.discountedPrice;
  }
);
</script>

<style lang="scss">
.add-new-shop-item {
  width: 100%;

  .error {
    color: red;
    margin-bottom: 0.5rem;
  }

  .section {
    font-weight: 400;
    margin-bottom: 0.25rem;
  }

  .p-inputtext,
  .p-inputnumber {
    width: 100%;
    margin-bottom: 0.5rem;
  }

  .service-info {
    display: flex;
    flex-direction: column;
    flex-shrink: 1;
  }

  .service-image {
    width: 100%;
    height: 6rem;
    margin-bottom: 0.5rem;

    .preview {
      display: flex;
      align-items: center;
      justify-content: space-evenly;
    }
  }
}
</style>
